import { Fragment, useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { ChevronDown } from "lucide-react";
import { ThreeDots } from "react-loader-spinner";

import { API_EXTENSION_REPORT } from "../../../../../config/Endpoints.js";
import NoRecordsAvailable from "../../../../../components/NoRecordsAvailable";
import { Badge, TablePagination, type BadgeVariant } from "../../../ui/index.ts";
import { showError } from "../../../../utils/toast.ts";
import type {
  EmployeeExtensionDetails,
  EmployeeExtensionSummary,
  ExtensionDetailsPage,
  ExtensionEntry,
  ExtensionReportFilters,
} from "../types.ts";

import styles from "./extension-report-table.module.css";

export interface ExtensionReportTableProps {
  data: EmployeeExtensionSummary[];
  filters: ExtensionReportFilters;
  loading?: boolean;
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

function formatDate(value: string | null): string {
  if (!value) return "-";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

function formatType(type: string): string {
  if (type.trim().toUpperCase() === "WFH") return "Work from Home";
  return type.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function statusDisplay(entry: ExtensionEntry): { label: string; variant: BadgeVariant } {
  const value = (entry.statusLabel || entry.status || "-").trim();
  const normalized = value.toUpperCase().replace(/_/g, "-");
  const label = normalized === "AUTO-APPROVED"
    ? "Auto-Approved"
    : value.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
  if (normalized.includes("APPROVED")) return { label, variant: "success" };
  if (normalized.includes("REJECTED")) return { label, variant: "danger" };
  if (normalized.includes("PENDING")) return { label, variant: "warning" };
  return { label, variant: "neutral" };
}

function detailParams(employeeId: number, filters: ExtensionReportFilters, page: number) {
  return {
    employeeId,
    page,
    size: 5,
    ...(filters.fromDate ? { fromDate: filters.fromDate } : {}),
    ...(filters.toDate ? { toDate: filters.toDate } : {}),
    ...(filters.status ? { status: filters.status } : {}),
  };
}

interface EmployeeDetailState {
  entries: ExtensionEntry[];
  page: number;
  totalElements: number;
  hasMore: boolean;
}

function parseDetailResponse(
  body: EmployeeExtensionDetails[] | ExtensionDetailsPage,
  employeeId: number,
  page: number,
): Omit<EmployeeDetailState, "page"> {
  const content = Array.isArray(body) ? body : Array.isArray(body.content) ? body.content : [];
  const first = content[0] as EmployeeExtensionDetails | ExtensionEntry | undefined;
  const entries = first && "extensions" in first
    ? (content as EmployeeExtensionDetails[]).find((item) => item.employeeId === employeeId)?.extensions ?? first.extensions ?? []
    : content as ExtensionEntry[];

  if (Array.isArray(body)) {
    return { entries, totalElements: entries.length, hasMore: false };
  }

  const totalElements = Math.max(Number(body.totalElements) || 0, entries.length);
  const hasMore = typeof body.last === "boolean"
    ? !body.last
    : Number(body.totalPages) > page + 1;
  return { entries, totalElements, hasMore };
}

export function ExtensionReportTable({
  data,
  filters,
  loading,
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
}: ExtensionReportTableProps) {
  const [expandedEmployeeId, setExpandedEmployeeId] = useState<number | null>(null);
  const [details, setDetails] = useState<Record<number, EmployeeDetailState>>({});
  const [detailLoadingId, setDetailLoadingId] = useState<number | null>(null);
  const token = Cookies.get("accessToken");

  useEffect(() => {
    setExpandedEmployeeId(null);
    setDetails({});
  }, [filters, page, pageSize]);

  const fetchEmployeeDetails = async (employeeId: number, detailPage: number, append: boolean) => {
    setDetailLoadingId(employeeId);
    try {
      const response = await axios.get<EmployeeExtensionDetails[] | ExtensionDetailsPage>(API_EXTENSION_REPORT, {
        params: detailParams(employeeId, filters, detailPage),
        headers: { Authorization: `Bearer ${token}` },
      });
      const parsed = parseDetailResponse(response.data, employeeId, detailPage);
      setDetails((current) => {
        const entries = append
          ? [...(current[employeeId]?.entries ?? []), ...parsed.entries]
          : parsed.entries;
        return {
          ...current,
          [employeeId]: {
            ...parsed,
            page: detailPage,
            entries,
            totalElements: Math.max(parsed.totalElements, entries.length),
          },
        };
      });
    } catch (error) {
      const message = axios.isAxiosError(error) && typeof error.response?.data?.message === "string"
        ? error.response.data.message
        : "Failed to load employee extension details.";
      showError(message);
      if (!append) {
        setDetails((current) => ({
          ...current,
          [employeeId]: { entries: [], page: 0, totalElements: 0, hasMore: false },
        }));
      }
    } finally {
      setDetailLoadingId(null);
    }
  };

  const toggleEmployee = async (employeeId: number) => {
    if (expandedEmployeeId === employeeId) {
      setExpandedEmployeeId(null);
      return;
    }
    setExpandedEmployeeId(employeeId);
    if (details[employeeId]) return;

    await fetchEmployeeDetails(employeeId, 0, false);
  };

  if (loading) {
    return <div className={styles.loadingState}>
      <ThreeDots height="80" width="80" color="var(--color-accent-500)" visible />
    </div>;
  }

  if (data.length === 0) return <NoRecordsAvailable />;

  return <div className={styles.container}>
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            <th className={styles.th} scope="col">Requested For</th>
            <th className={styles.th} scope="col">Requested On</th>
            <th className={styles.th} scope="col">Type</th>
            <th className={styles.th} scope="col">Status</th>
            <th className={styles.th} scope="col">Attempts Balance</th>
            <th className={styles.th} scope="col">Total Extensions</th>
          </tr>
        </thead>
        <tbody>
          {data.map((employee, index) => {
            const expanded = expandedEmployeeId === employee.employeeId;
            const employeeDetails = details[employee.employeeId];
            return <Fragment key={employee.employeeId}>
              <tr className={styles.sectionHeaderRow}>
                <th className={styles.sectionHeader} scope="colgroup" colSpan={6}>
                  <div className={styles.summaryRow}>
                    <span className={styles.sectionSerial}>
                      {employee.srNo || (page - 1) * pageSize + index + 1}.
                    </span>
                    {/* <span className={styles.sectionEmployeeLabel}>Employee:</span> */}
                    <span className="heading-xs">{employee.employeeName || "-"}</span>
                    <span className={styles.sectionBadges}>
                      <Badge variant="warning">HR Extensions: {employee.hrExtension ?? 0}</Badge>
                      <Badge variant="success">Self Extensions: {employee.selfExtension ?? 0}</Badge>
                    </span>
                    <span className={styles.actionCell}>
                      <button
                        type="button"
                        className={styles.toggleButton}
                        aria-expanded={expanded}
                        aria-label={`${expanded ? "Collapse" : "Expand"} extension details for ${employee.employeeName}`}
                        onClick={() => void toggleEmployee(employee.employeeId)}
                      >
                        <ChevronDown
                          className={expanded ? styles.chevronOpen : undefined}
                          size={20}
                          aria-hidden
                        />
                      </button>
                    </span>
                  </div>
                </th>
              </tr>
              {expanded && detailLoadingId === employee.employeeId && !employeeDetails ? (
                <tr><td className={styles.detailCell} colSpan={6}><div className="d-flex justify-content-center"><ThreeDots height="36" width="36" color="var(--color-accent-500)" visible  /></div></td></tr>
              ) : null}
              {expanded && detailLoadingId !== employee.employeeId && employeeDetails?.entries.length === 0 ? (
                <tr><td className={styles.detailCell} colSpan={6}>No extension records found.</td></tr>
              ) : null}
              {expanded ? employeeDetails?.entries.map((entry, detailIndex) => {
                const status = statusDisplay(entry);
                return <tr className={styles.tr} key={`${employee.employeeId}-${entry.requestedFor}-${entry.requestedOn}-${detailIndex}`}>
                  <td className={styles.td}>{formatDate(entry.requestedFor)}</td>
                  <td className={styles.td}>{formatDate(entry.requestedOn)}</td>
                  <td className={styles.td}><Badge variant="neutral">{formatType(entry.type)}</Badge></td>
                  <td className={styles.td}><Badge variant={status.variant}>{status.label}</Badge></td>
                  <td className={`${styles.td} ${styles.numberCell}`}>{entry.attemptsBalance ?? "-"}</td>
                  <td className={`${styles.td} ${styles.numberCell}`}>{entry.totalExtensions ?? 0}</td>
                </tr>;
              }) : null}
              {expanded && employeeDetails?.hasMore ? (
                <tr>
                  <td className={styles.detailCell} colSpan={6}>
                    <div className={styles.innerLoadMore}>
                      <span className={styles.innerPaginationRange}>
                        Showing {employeeDetails.entries.length} of {employeeDetails.totalElements}
                      </span>
                      <button
                        type="button"
                        className={styles.loadMoreLink}
                        disabled={detailLoadingId === employee.employeeId}
                        onClick={() => void fetchEmployeeDetails(employee.employeeId, employeeDetails.page + 1, true)}
                      >
                        {detailLoadingId === employee.employeeId ? "Loading..." : "Load more"}
                      </button>
                    </div>
                  </td>
                </tr>
              ) : null}
            </Fragment>;
          })}
        </tbody>
      </table>
    </div>
    <TablePagination
      page={page}
      pageSize={pageSize}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      pageSizeOptions={[5, 10, 20, 30, 50]}
    />
  </div>;
}

export default ExtensionReportTable;
